/** routeSlotId-keyed analogue of `payloads.ts`; a row missing a slot id is reported, never dropped silently. */

import type { DatedRouteSlot, SlotSequence } from 'types/api/route/routeSlotRequests'
import type { RouteSlotId } from 'types/entities/shared/ids'

/** Minimal shape every helper here reads — satisfied by `DailyStop`, `OnRequestStop` and `MasterStop`. */
export interface SlotRow {
  readonly routeSlotId?: RouteSlotId | null
}

export interface BuildSlotSequencePairsResult {
  readonly pairs: ReadonlyArray<SlotSequence>
  readonly droppedDuplicates: ReadonlyArray<RouteSlotId>
  readonly droppedMissing: number
}

/** Dedupes and renumbers — the slot-keyed guard against the duplicate-sequence bug `buildSequencePairs` closes. */
export const buildSlotSequencePairs = (rows: ReadonlyArray<SlotRow>, startSequence = 1): BuildSlotSequencePairsResult => {
  const seen = new Set<string>()
  const pairs: SlotSequence[] = []
  const droppedDuplicates: RouteSlotId[] = []
  let droppedMissing = 0

  rows.forEach(row => {
    const { routeSlotId } = row
    if (!routeSlotId) {
      droppedMissing += 1
      return
    }
    if (seen.has(routeSlotId)) {
      droppedDuplicates.push(routeSlotId)
      return
    }
    seen.add(routeSlotId)
    pairs.push({ routeSlotId, sequence: startSequence + pairs.length })
  })

  return { pairs, droppedDuplicates, droppedMissing }
}

/** Slot ids in row order, deduped. For the bodies that take a bare id list (unassign, day-of-week). */
export const collectRouteSlotIds = (rows: ReadonlyArray<SlotRow>): ReadonlyArray<RouteSlotId> => {
  const seen = new Set<string>()
  const ids: RouteSlotId[] = []
  rows.forEach(({ routeSlotId }) => {
    if (!routeSlotId || seen.has(routeSlotId)) return
    seen.add(routeSlotId)
    ids.push(routeSlotId)
  })
  return ids
}

export const collectRowsMissingRouteSlotId = <T extends SlotRow>(rows: ReadonlyArray<T>): ReadonlyArray<T> =>
  rows.filter(row => !row.routeSlotId)

/** Stamps the date these rows sit on NOW, not the destination sent alongside it. */
export const toDatedRouteSlots = (rows: ReadonlyArray<SlotRow>, serviceDate: string): DatedRouteSlot[] =>
  collectRouteSlotIds(rows).map(routeSlotId => ({ routeSlotId, serviceDate }))

/** Membership must come from the master read: a temp-moved recurring stop carries a CSE without being on master. */
export const collectMasterRouteSlotIds = (masterStops: ReadonlyArray<SlotRow>): ReadonlySet<string> =>
  new Set(collectRouteSlotIds(masterStops))

/** Narrowed to slots master holds — submitting one it doesn't makes `ValidateSequenceOnRoute` reject the call. */
export const toMasterSlotSequenceEntries = (
  rows: ReadonlyArray<SlotRow>,
  masterRouteSlotIds: ReadonlySet<string>
): ReadonlyArray<SlotSequence> =>
  buildSlotSequencePairs(rows.filter(row => row.routeSlotId != null && masterRouteSlotIds.has(row.routeSlotId))).pairs

/** The anchor `moveRouteSlots` inserts after; the top position sends null. */
export const computeTargetRouteSlotId = (orderedRows: ReadonlyArray<SlotRow>, newIndex: number): RouteSlotId | null => {
  if (newIndex <= 0) return null
  return orderedRows[newIndex - 1]?.routeSlotId ?? null
}

export interface RouteSlotMoveRun {
  readonly targetRouteSlotId: RouteSlotId | null
  readonly slots: ReadonlyArray<DatedRouteSlot>
}

/** Contiguous runs of moved rows, each anchored on the settled row above — the endpoint takes one `targetStopId`. */
export const buildRouteSlotMoveRuns = <T extends SlotRow>(
  destinationOrderedRows: ReadonlyArray<T>,
  isMoved: (row: T) => boolean,
  serviceDate: string
): ReadonlyArray<RouteSlotMoveRun> => {
  const runs: RouteSlotMoveRun[] = []
  let lastSettledId: RouteSlotId | null = null
  let current: DatedRouteSlot[] | null = null
  let currentAnchor: RouteSlotId | null = null

  const flush = () => {
    if (current && current.length > 0) runs.push({ targetRouteSlotId: currentAnchor, slots: current })
    current = null
    currentAnchor = null
  }

  destinationOrderedRows.forEach(row => {
    if (!isMoved(row)) {
      flush()
      lastSettledId = row.routeSlotId ?? lastSettledId
      return
    }
    if (!row.routeSlotId) return
    if (!current) {
      current = []
      currentAnchor = lastSettledId
    }
    current.push({ routeSlotId: row.routeSlotId, serviceDate })
  })
  flush()

  return runs
}
